import { useRef, Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import { Row, Col, Button, Form } from 'react-bootstrap';
import compose from 'recompose/compose';
import { CheckBox, TextArea, Switch } from 'components';
import { updateGiftTable } from 'store/actions/giftTable';
import { withStore } from 'store';
import { transformForSelect } from 'util/transformHelpers';
import { fetchGiftTableMessageTypes } from 'store/actions/catalogs';
import { THANKS_MESSAGES_TYPES } from 'constants/catalogs/thanksMessagesTypes';
import { PLAN_TYPES } from 'constants/catalogs/planTypes';
import {
  THANKS_MESSAGE_OPTIONS,
  DEFAULT_VALUE_THANKS_MSG,
  validationSchema,
  mapPropsToValues
} from './config';
import { formatter } from './formatter';
import styles from './styles.module.scss';

function Message({
  dispatch,
  thanksMessagesTypes = [],
  tableInfo,
  values,
  setFieldValue
}) {
  const thanksMessageTextArea = useRef(null);

  const { thanksMessage } = values;

  const handleAddThanksMessageOption = (text) => () => {
    const textarea = thanksMessageTextArea.current;

    if (textarea) {
      const indexAt = textarea.selectionStart;
      const notSelected = indexAt === 0 && textarea.selectionEnd === 0;

      const firstPart = thanksMessage.slice(0, indexAt);
      const lastPart = thanksMessage.slice(indexAt + 1, thanksMessage.length);

      const firstSpace = !firstPart.endsWith(' ') ? ' ' : '';
      const lastSpace = !lastPart.startsWith(' ') ? ' ' : '';
      const notSelectedSpace = !thanksMessage.endsWith(' ') ? ' ' : '';

      const newText = notSelected
        ? `${thanksMessage}${notSelectedSpace}${text}`
        : `${firstPart}${firstSpace}${text}${lastSpace}${lastPart}`;

      setFieldValue('thanksMessage', newText);

      thanksMessageTextArea.current?.focus();
      thanksMessageTextArea.current?.setSelectionRange(2, 5);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(updateGiftTable({ ...formatter(values), withLoader: true }));
  };

  useEffect(() => {
    dispatch(fetchGiftTableMessageTypes(false));
  }, []);

  useEffect(() => {
    setFieldValue('messageType', tableInfo?.thanks_message?.type_id);
    setFieldValue(
      'thanksMessage',
      tableInfo?.thanks_message?.message || DEFAULT_VALUE_THANKS_MSG
    );
  }, [setFieldValue, tableInfo]);

  return (
    <section className={styles.message}>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col xs={12}>
            <h6 className="font-weight-bold ml-1">Mensaje de agradecimiento</h6>
          </Col>
          <Col xs={12}>
            {transformForSelect(thanksMessagesTypes, 'id', 'name').map(
              ({ id, value, label }) => (
                <Fragment key={id}>
                  {value !== thanksMessagesTypes[0].id && (
                    <CheckBox
                      type="radio"
                      name="messageType"
                      label={label}
                      value={value}
                      removable
                    />
                  )}
                </Fragment>
              )
            )}
          </Col>
          {values.messageType === THANKS_MESSAGES_TYPES.COMMON_MESSAGE && (
            <>
              <Col xs={12}>
                <TextArea
                  ref={thanksMessageTextArea}
                  id="thanksMessage"
                  className={`${styles.thanksMessageInput} overflow-hidden`}
                  name="thanksMessage"
                  label=""
                  maxLength={false}
                />
              </Col>
              <Col
                xs={12}
                className="d-flex justify-content-around align-items-center flex-wrap mb-3"
              >
                <div className="my-2">
                  <p className="m-0">
                    <strong>$DATO:</strong> Se reeplazará con el dato del evento
                  </p>
                </div>
                <div className="my-2 text-center">
                  {THANKS_MESSAGE_OPTIONS.map(({ id, text }) => (
                    <Button
                      key={id}
                      variant="secondary"
                      className={styles.messageButton}
                      onClick={handleAddThanksMessageOption(text)}
                    >
                      {text}
                    </Button>
                  ))}
                </div>
              </Col>
            </>
          )}

          {tableInfo?.plan?.id === PLAN_TYPES.GIFTS_GIFTS_AND_GIFTS && (
            <Col xs={12} className="d-flex mt-2 m-md-0">
              <Switch name="keepShowingProducts" label="" />

              <div className="ml-1">
                <h5 className="mb-3 mb-md-1">
                  Seguir mostrando los productos que ya se compraron
                </h5>
                <h6>
                  <strong>Importante:</strong> Al activar esta opción los
                  productos podrán ser comprados aunque haya excedido la
                  cantidad agregada a la mesa de regalos.
                </h6>
              </div>
            </Col>
          )}

          <Col xs={12} className="my-3 mt-md-5 d-flex justify-content-center">
            <Button type="submit" className={styles.updateButton}>
              Actualizar
            </Button>
          </Col>
        </Row>
      </Form>
    </section>
  );
}

Message.propTypes = {
  dispatch: PropTypes.func.isRequired,
  tableInfo: PropTypes.shape({
    thanks_message: PropTypes.shape({
      type_id: PropTypes.number,
      message: PropTypes.string
    }),
    plan: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  }),
  values: PropTypes.shape({
    messageType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    thanksMessage: PropTypes.string,
    keepShowingProducts: PropTypes.bool
  }),
  setFieldValue: PropTypes.func.isRequired,
  thanksMessagesTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object
      ]),
      label: PropTypes.string
    })
  )
};

export default compose(
  withStore({
    mapStateToProps: (state) => ({
      thanksMessagesTypes: state.catalogs.thanksMessagesTypes,
      tableInfo: state.giftTable.giftTableInfo.data
    })
  }),
  withFormik({ mapPropsToValues, validationSchema })
)(Message);
